import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { automationAPI, agentsAPI, teamsAPI, labelsAPI, complaintTicketsAPI } from '../../services/api';
import { toast } from '../ui/use-toast';
import AutomationFlowEditor from './AutomationFlowEditor';
import { tagTree, stripIds, type ActionNode } from './automationActionTree';
import { TICKET_EVENTS } from './automationConstants';

interface AutomationRuleEditorProps {
  ruleId?: number | null;
  // §3.7: pre-fills the (blank) create form from a starter template instead
  // of an existing saved rule — same shape as what loadRule() would produce,
  // just sourced from the static catalog rather than the API.
  initialTemplate?: {
    name: string;
    description?: string;
    trigger_event: string;
    conditions: any[];
    actions: any[];
  } | null;
  onSaved: (rule: any) => void;
  // 2026-09-01: Save lives in AutomationRuleEditorPage.tsx's own header
  // now (see this component's own return below) — the page needs to know
  // whether a save is in flight and whether one is currently valid to
  // enable/disable its button, without owning formData/conditions/actions
  // itself. Fired on every change to those three, same dependency set
  // handleSubmit's own disabled check used before this component's return
  // JSX still checked it locally.
  onStateChange?: (state: { saving: boolean; canSave: boolean }) => void;
}

export interface AutomationRuleEditorHandle {
  save: () => void;
}

/**
 * 2026-08-09: the rule editor, embedded directly in AutomationRulesPage's
 * main content area — extracted out of what used to be AutomationRuleDialog
 * (a modal) so the canvas can be the page's default view instead of
 * something that only appears in a popup. Nothing about the editing logic
 * changed in this extraction, only the outer chrome: no Dialog/DialogContent
 * wrapper, no open/onClose — the page decides what's "selected" and mounts
 * this with a fresh `key` per rule (see AutomationRulesPage.tsx) so internal
 * state resets cleanly between rules without needing an imperative reset.
 *
 * 2026-09-01: now a forwardRef exposing `save()` (via useImperativeHandle)
 * so AutomationRuleEditorPage.tsx's own header can trigger a save without
 * this component rendering its own separate Save button/header row --
 * matches the one-header mockup this was built from (Docs/mockups/
 * automation-rule-editor-canvas.html) rather than two stacked ones.
 */
const AutomationRuleEditor = forwardRef<AutomationRuleEditorHandle, AutomationRuleEditorProps>(({
  ruleId,
  initialTemplate,
  onSaved,
  onStateChange,
}, ref) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_event: 'conversation_created',
    enabled: true,
    priority: 0,
    stop_processing: false,
  });

  const [conditions, setConditions] = useState<any[]>([
    { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' },
  ]);

  const [actions, setActions] = useState<ActionNode[]>(
    tagTree([{ action_type: 'assign_agent', action_parameters: {}, execution_order: 0 }])
  );
  // 2026-09-01 (Docs/AUTOMATION_VISUAL_WORKFLOW_BUILDER_PLAN.md Phase 2,
  // freeform drag): {node_id: {x, y}} for nodes the admin has manually
  // repositioned -- a node with no entry here still gets the layout
  // engine's own auto-computed position (see AutomationFlowEditor.tsx).
  // Pure display metadata, saved/loaded alongside the rule but never read
  // by execution -- see canvas_layout's own comment on the backend model.
  const [canvasLayout, setCanvasLayout] = useState<Record<string, { x: number; y: number }>>({});
  const [agents, setAgents] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [labels, setLabels] = useState<any[]>([]);
  const [categories, setCategories] = useState<{ id: string; label: string }[]>([]);
  const [templates, setTemplates] = useState<{ id: number; name: string; language?: string }[]>([]);
  const [channels, setChannels] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void loadResources();
    if (ruleId) void loadRule();
    else if (initialTemplate) applyTemplate(initialTemplate);
    else resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyTemplate = (template: NonNullable<AutomationRuleEditorProps['initialTemplate']>) => {
    setFormData({
      name: template.name,
      description: template.description || '',
      trigger_event: template.trigger_event,
      enabled: true,
      priority: 0,
      stop_processing: false,
    });
    setConditions(
      template.conditions.length > 0
        ? template.conditions
        : [{ field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' }]
    );
    const tagged = tagTree(
      template.actions.length > 0
        ? template.actions
        : [{ action_type: 'assign_agent', action_parameters: {}, execution_order: 0 }]
    );
    setActions(tagged);
  };

  const loadResources = async () => {
    try {
      const [agentsData, teamsData, labelsData, ticketConfig, ticketConfigOptions] = await Promise.all([
        agentsAPI.getAgents().catch(() => ({ agents: [] })),
        teamsAPI.getTeams().catch(() => ({ teams: [] })),
        labelsAPI.getLabels().catch(() => ({ labels: [] })),
        complaintTicketsAPI.getConfig().catch(() => null),
        complaintTicketsAPI.getConfigOptions().catch(() => null),
      ]);
      setAgents(agentsData.agents || agentsData || []);
      setTeams(teamsData.teams || teamsData || []);
      setLabels(labelsData.labels || labelsData || []);
      const rawCategories = (ticketConfig?.config?.categories || []) as any[];
      setCategories(rawCategories.map((c) => ({ id: c.id, label: c.labels?.en || c.id })));
      setTemplates(ticketConfigOptions?.templates || []);
      setChannels(ticketConfigOptions?.channels || []);
    } catch (err) {
      console.error('Failed to load resources:', err);
    }
  };

  const loadRule = async () => {
    try {
      setLoading(true);
      const rule = await automationAPI.getAutomationRule(ruleId!);
      setFormData({
        name: rule.name,
        description: rule.description || '',
        trigger_event: rule.trigger_event,
        enabled: rule.enabled,
        priority: rule.priority ?? 0,
        stop_processing: rule.stop_processing ?? false,
      });
      setCanvasLayout(rule.canvas_layout || {});
      if (rule.conditions && rule.conditions.length > 0) setConditions(rule.conditions);
      if (rule.actions && rule.actions.length > 0) {
        const tagged = tagTree(rule.actions);
        setActions(tagged);
      }
    } catch (err) {
      console.error('Failed to load rule:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger_event: 'conversation_created',
      enabled: true,
      priority: 0,
      stop_processing: false,
    });
    setConditions([
      { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' },
    ]);
    setActions(tagTree([{ action_type: 'assign_agent', action_parameters: {}, execution_order: 0 }]));
    setCanvasLayout({});
  };

  // 2026-09-07: a FastAPI 422 validation failure (e.g. the backend's
  // request_profile_field param validator rejecting an empty field_key/
  // prompt_text) returns `detail` as an ARRAY of Pydantic error objects
  // ({type, loc, msg, input, ctx}), not a string. Passing that straight
  // into toast()'s description rendered it as a React child and crashed
  // the whole page (React error #31, "Objects are not valid as a React
  // child") -- a pre-existing gap this surfaced, not something specific to
  // that one validator. Same pattern already used in RoleManagementPage.tsx.
  const formatErrorMessage = (error: any): string => {
    if (error?.response?.data) {
      if (Array.isArray(error.response.data.detail)) {
        return error.response.data.detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
      }
      if (typeof error.response.data.detail === 'string') return error.response.data.detail;
      if (error.response.data.message) return error.response.data.message;
    }
    return error?.message || 'Unknown error';
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const payload = {
        ...formData,
        conditions: conditions.map((c) => ({
          ...c,
          condition_group: c.condition_group || 'default',
        })),
        actions: stripIds(actions).map((a, i) => ({
          ...a,
          execution_order: a.execution_order !== undefined ? a.execution_order : i,
        })),
        canvas_layout: canvasLayout,
      };

      const saved = ruleId
        ? await automationAPI.updateAutomationRule(ruleId, payload)
        : await automationAPI.createAutomationRule(payload);

      toast({ title: ruleId ? 'Rule updated' : 'Rule created' });
      onSaved(saved);
    } catch (err: any) {
      console.error('Failed to save rule:', err);
      toast({
        title: 'Could not save rule',
        description: formatErrorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: () => void handleSubmit(),
  }));

  // Same disabled-check handleSubmit's own Save button used to compute
  // locally, before that button moved to AutomationRuleEditorPage.tsx's
  // header -- the page needs it to enable/disable its own button.
  useEffect(() => {
    onStateChange?.({
      saving,
      canSave: !saving && !!formData.name && conditions.length > 0 && actions.length > 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, formData.name, conditions.length, actions.length]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  // 2026-09-01 (live feedback — "everything should be in canvas... full
  // page canvas look and feel"): the standalone "Rule information" form and
  // the section header/Save button that used to live above the canvas are
  // gone. Rule name/trigger event/description/enable/priority/stop_processing
  // are now edited in place inside the Trigger node itself (see
  // AutomationFlowNodes.tsx's TriggerFlowNode, fed by formData/
  // onFormDataChange below) — the canvas is the entire editing surface, no
  // separate form above or beside it. Save moved into
  // AutomationRuleEditorPage.tsx's own header bar (this component exposes
  // it via useImperativeHandle below) so there's exactly one header, not
  // two, matching the mockup this was built from.
  return (
    <div className="h-full">
      <AutomationFlowEditor
        triggerEvent={formData.trigger_event}
        formData={formData}
        onFormDataChange={setFormData}
        conditions={conditions}
        onConditionsChange={setConditions}
        actions={actions}
        onChange={setActions}
        layout={canvasLayout}
        onLayoutChange={setCanvasLayout}
        isTicketEvent={TICKET_EVENTS.has(formData.trigger_event)}
        agents={agents}
        teams={teams}
        labels={labels}
        categories={categories}
        templates={templates}
        channels={channels}
      />
    </div>
  );
});

export default AutomationRuleEditor;
